//@flow
import React from 'react'
import moment from 'moment'

import { Flex, Table, Checkbox } from '..'
import FileManager from './FileManager'
import PathLocation from './pathLocation'
import Filename from './filename'
import { useI18n } from '~/core/i18n'

type FileData = {
  path: string,
  filename: string,
  fileType: string
}

type FileManagerData = Array<{
  path: string,
  size: number,
  lastUpdated: string | number,
  [key: string]: any
}>

const generateColumns = (
  msg: (path: string) => string,
  checkedRows: string[],
  onRowCheck?: (path: string) => void,
  onRowCheckAll?: (paths: string[]) => void,
  onAscend: () => void,
  onDescend: (pathSegment: string) => void,
  onFileClick?: (fileData: FileData) => void
) =>
  [
    typeof onRowCheck === 'function' && {
      accessor: 'checkbox',
      resizable: false,
      sortable: false,
      width: 50,
      style: {
        display: 'flex',
        justifyContent: 'center'
      },
      Header: ({ data }: any) => {
        const filteredData = data.filter(({ _original }) => !_original.isAscendSelector && !_original.isDirectory)
        const isAllChecked = checkedRows.length && checkedRows.length === filteredData.length
        return typeof onRowCheckAll === 'function' ? (
          <Checkbox
            checked={!!isAllChecked}
            isDisabled={!filteredData.length}
            style={{ transform: 'translateY(2px)' }}
            onChange={() => onRowCheckAll(isAllChecked ? [] : filteredData.map(({ _original }) => _original.path))}
          />
        ) : null
      },
      Cell: ({ original: { path, isAscendSelector, isDirectory } }: any) => {
        const isChecked = checkedRows.includes(path)
        return isDirectory || isAscendSelector ? (
          <Checkbox isDisabled />
        ) : (
          <Checkbox checked={isChecked} onChange={() => onRowCheck(path)} />
        )
      }
    },
    {
      id: 'filename',
      accessor: row => ({
        filename: row.filename,
        isDirectory: !!row.isDirectory,
        isAscendSelector: !!row.isAscendSelector
      }),
      Header: msg('common.fileManager.filename'),
      width: 300,
      Cell: ({ original: { filename, path, fileType, isDirectory, isAscendSelector } }: any) => (
        <Filename
          id={path}
          isAscendSelector={isAscendSelector}
          isClickable={isAscendSelector || isDirectory || typeof onFileClick === 'function'}
          isDirectory={isDirectory}
          type={fileType}
          onClick={() => {
            if (isAscendSelector) {
              return onAscend()
            }
            if (isDirectory) {
              return onDescend(filename)
            }

            if (typeof onFileClick === 'function') {
              return onFileClick({
                path,
                filename,
                fileType
              })
            }
          }}
        >
          {filename}
        </Filename>
      ),
      sortMethod: FileManager.sortNameColumn
    },
    {
      id: 'size',
      accessor: row => ({
        size: row.size,
        isDirectory: !!row.isDirectory,
        isAscendSelector: !!row.isAscendSelector
      }),
      Header: msg('common.fileManager.size'),
      Cell: ({ original: { size } }) => (size > 0 ? FileManager.formatFileSize(size) : '-'),
      sortMethod: FileManager.sortSizeColumn
    },
    {
      accessor: 'lastUpdated',
      Header: msg('common.fileManager.lastUpdated'),
      minWidth: 200,
      sortable: false,
      Cell: ({ original: { lastUpdated } }) => (lastUpdated ? moment(lastUpdated).format('YYYY-MM-DD HH:mm:ss') : '-')
    }
  ].filter(Boolean)

type FileManagerTableProps = {
  data: FileManagerData,
  path: string[],
  checkedRows?: string[],
  customColumns?: Array<{
    [key: string]: any
  }>,
  onFileClick?: (fileData: FileData) => void,
  onPathChange: (path: string[]) => void,
  onRowCheck?: (path: string) => void,
  onRowCheckAll?: (paths: string[]) => void
}

const FileManagerTable = ({
  checkedRows = [],
  customColumns = [],
  data,
  path,
  onPathChange,
  onFileClick,
  onRowCheck = () => {},
  onRowCheckAll,
  ...props
}: FileManagerTableProps) => {
  const [msg] = useI18n()
  const tableData = FileManager.getHierarchyStructure(data, path)

  const handleAscendHierarchy = () => {
    const newPath = path.slice(0, path.length - 1)
    return onPathChange(newPath)
  }

  const handleDescendHierarchy = (pathSegment: string) => {
    const newPath = [...path, pathSegment]
    return onPathChange(newPath)
  }

  return (
    <Flex column>
      <PathLocation path={path} onPathChange={onPathChange} />
      <Table
        columns={[
          ...generateColumns(
            msg,
            checkedRows,
            onRowCheck,
            onRowCheckAll,
            handleAscendHierarchy,
            handleDescendHierarchy,
            onFileClick
          ),
          ...customColumns
        ]}
        data={tableData}
        defaultSorted={[
          {
            id: 'filename',
            desc: false
          }
        ]}
        pageSize={tableData.length < 5 ? 5 : tableData.length}
        {...props}
      />
    </Flex>
  )
}

export default FileManagerTable
